import { createSlice } from '@reduxjs/toolkit';
import { sanitizeKaBucketPageLabels } from '../../../constants/collections/KAActionBucketPageDataLabels';
import collectionsState from '../../../constants/collections-v1/collectionsState';

// Create a Redux slice for the 'Collections' portion of the state
export const collectionsSlice = createSlice({
  name: 'collectionsV1', // The name of the slice
  initialState: collectionsState, // Initial state for Collections
  reducers: {
    // Action to set Collections
    setCollections: (state, action) => {
      state.data = action.payload;
    },
    // Action to set Collections Etc data
    setCollectionsEtc: (state, action) => {
      state.etc = action.payload;
    },
    // Action to set Collections step
    setCollectionsStep: (state, action) => {
      state.etc.collectionsStep = action.payload;
    },
    // Action to set the next Collections step
    setNextCollectionsStep: state => {
      state.etc.collectionsStep = state.etc.collectionsStep + 1;
    },
    // Action to set the previous Collections step
    setPrevCollectionsStep: state => {
      state.etc.collectionsStep = state.etc.collectionsStep - 1;
    },
    // Action to set whether Collections is submittable
    setIsCollectionsSubmittable: (state, action) => {
      state.etc.isCollectionsSubmittable = action.payload;
    },
    // Action to set the bucket type
    setBucketType: (state, action) => {
      state.data.actionBucketData.bucketType = action.payload;
    },

    setCollectionTitle: (state, action) => {
      state.data.title = action.payload;
    },

    setCollectionDomainId: (state, action) => {
      state.data.domainId = action.payload;
    },

    setCollectionEngine: (state, action) => {
      state.data.actionBucketData.engine = action.payload;
    },

    setCollectionPreferredMethod: (state, action) => {
      state.etc.collectionType = action.payload;
    },

    setSelectedUrlsToCrawl: (state, action) => {
      state.etc.selectedUrlsToCrawl = action.payload;
    },

    setIsContentMappingCompleted: (state, action) => {
      state.etc.isContentMappingCompleted = action.payload;
    },

    setIsWordpressConnected: (state, action) => {
      state.etc.isWordpressConnected = action.payload;
    },

    setKAUploadedDataCount: (state, action) => {
      state.etc.KAUploadedDataCount = action.payload;
    },

    setSelectedAssetsToCrawlType: (state, action) => {
      state.etc.selectedAssetsToCrawlType = action.payload;
    },

    setPageIds: (state, action) => {
      state.data.actionBucketData.pageIds = action.payload;
    },

    setTargetPagesList: (state, action) => {
      state.etc.targetPagesList = action.payload.map(page => ({
        ...page,
        labels: sanitizeKaBucketPageLabels(page.labels),
        isSpecialCrawlingActive: !!page.isSpecialCrawlingActive,
      }));
      state.data.actionBucketData.pageIds = state.etc.targetPagesList.map(page => page._id);
    },

    updateTargetPageLabels: (state, action) => {
      const { pageId, labels } = action.payload;
      const index = state.etc.targetPagesList.findIndex(page => page._id === pageId);
      if (index === -1) return;
      const sanitizedLabels = sanitizeKaBucketPageLabels(labels);
      state.etc.targetPagesList[index].labels = sanitizedLabels;
      if (!sanitizedLabels.length) {
        state.etc.targetPagesList[index].isSpecialCrawlingActive = false;
      }
    },

    updateTargetPageSpecialCrawling: (state, action) => {
      const { pageId, isSpecialCrawlingActive } = action.payload;
      const index = state.etc.targetPagesList.findIndex(page => page._id === pageId);
      if (index === -1) return;
      const hasLabels = (state.etc.targetPagesList[index].labels || []).length > 0;
      state.etc.targetPagesList[index].isSpecialCrawlingActive =
        hasLabels && !!isSpecialCrawlingActive;
    },

    setDocumentIds: (state, action) => {
      state.data.actionBucketData.documentIds = action.payload;
    },

    updateActionBucketDocumentLabels: (state, action) => {
      const { mediaId, labels } = action.payload;
      if (!mediaId) return;
      const docs = state.data.actionBucketData.documentIds;
      const idx = docs.findIndex(d => d.mediaId === mediaId);
      if (idx === -1) return;
      state.data.actionBucketData.documentIds[idx].labels = sanitizeKaBucketPageLabels(labels);
    },

    setKaManualUploadPageIdsWithLabels: (state, action) => {
      const raw = Array.isArray(action.payload) ? action.payload : [];
      state.etc.kaManualUploadPageIdsWithLabels = raw.map(item => {
        const labels = sanitizeKaBucketPageLabels(item.labels);
        return {
          pageId: item.pageId,
          labels,
          isSpecialCrawlingActive: labels.length > 0 && !!item.isSpecialCrawlingActive,
          url: typeof item.url === 'string' ? item.url : '',
          title: typeof item.title === 'string' ? item.title : '',
        };
      });
    },

    updateKaManualUploadPageLabels: (state, action) => {
      const { pageId, labels } = action.payload;
      if (!pageId) return;
      const list = state.etc.kaManualUploadPageIdsWithLabels;
      const idx = list.findIndex(row => row.pageId === pageId);
      if (idx === -1) return;
      const sanitizedLabels = sanitizeKaBucketPageLabels(labels);
      list[idx].labels = sanitizedLabels;
      if (!sanitizedLabels.length) {
        list[idx].isSpecialCrawlingActive = false;
      }
    },

    updateKaManualUploadPageSpecialCrawling: (state, action) => {
      const { pageId, isSpecialCrawlingActive } = action.payload;
      if (!pageId) return;
      const list = state.etc.kaManualUploadPageIdsWithLabels;
      const idx = list.findIndex(row => row.pageId === pageId);
      if (idx === -1) return;
      const hasLabels = (list[idx].labels || []).length > 0;
      list[idx].isSpecialCrawlingActive = hasLabels && !!isSpecialCrawlingActive;
    },

    setSelectedRowKeys: (state, action) => {
      state.etc.selectedRowKeys = action.payload;
    },

    setSelectedRows: (state, action) => {
      state.etc.selectedRows = action.payload;
    },

    setIsAllPagesSelected: (state, action) => {
      state.data.actionBucketData.isAllPagesSelected = action.payload;
    },

    setPageSelectionType: (state, action) => {
      state.data.actionBucketData.pageSelectionType = action.payload;
    },

    setCriteriaFilteredListCount: (state, action) => {
      state.etc.criteriaFilteredListCount = action.payload;
    },

    setCriteria: (state, action) => {
      state.data.actionBucketData.criteria = action.payload;
    },

    deleteTargetPage: (state, action) => {
      const deletedPage = state.etc.targetPagesList.find(page => page._id === action.payload);
      state.etc.targetPagesList = state.etc.targetPagesList.filter(
        page => page._id !== action.payload
      );
      state.data.actionBucketData.pageIds = state.etc.targetPagesList.map(page => page._id);
      // Remove the page from selectedRowKeys if it exists
      if (deletedPage) {
        state.etc.selectedRowKeys = state.etc.selectedRowKeys.filter(
          key => key !== deletedPage.key
        );
        // Remove from selectedRows as well
        state.etc.selectedRows = state.etc.selectedRows.filter(row => row.key !== deletedPage.key);
      }
    },

    setIsAuthRequired: (state, action) => {
      state.etc.isAuthRequired = action.payload;
      if (!action.payload) {
        state.data.actionBucketData.contentExtractionAuthConfig = {
          loginURL: '',
          username: '',
          password: '',
        };
      }
    },

    setContentExtractionAuthConfig: (state, action) => {
      state.data.actionBucketData.contentExtractionAuthConfig = {
        ...state.data.actionBucketData.contentExtractionAuthConfig,
        ...action.payload,
      };
    },
  },
});

// Export the action creators
export const {
  setCollections,
  setCollectionsEtc,
  setCollectionsStep,
  setNextCollectionsStep,
  setPrevCollectionsStep,
  setIsCollectionsSubmittable,
  setBucketType,
  setCollectionTitle,
  setCollectionDomainId,
  setCollectionEngine,
  setCollectionPreferredMethod,
  setSelectedUrlsToCrawl,
  setIsContentMappingCompleted,
  setKAUploadedDataCount,
  setSelectedAssetsToCrawlType,
  setPageIds,
  setDocumentIds,
  updateActionBucketDocumentLabels,
  setKaManualUploadPageIdsWithLabels,
  updateKaManualUploadPageLabels,
  updateKaManualUploadPageSpecialCrawling,
  setTargetPagesList,
  updateTargetPageLabels,
  updateTargetPageSpecialCrawling,
  setSelectedRowKeys,
  setSelectedRows,
  setIsAllPagesSelected,
  setPageSelectionType,
  setIsWordpressConnected,
  setCriteriaFilteredListCount,
  setCriteria,
  deleteTargetPage,
  setIsAuthRequired,
  setContentExtractionAuthConfig,
} = collectionsSlice.actions;

// Export the reducer
export default collectionsSlice.reducer;
